"use client";

import type { JSX } from "react";
import { useState } from "react";
import {
  Button,
  FileUploadField,
  Icon,
  Input,
  MarkdownContent,
  Text,
  View
} from "@/components";
import { useLayout } from "@/providers";
import type { ContentSection } from "@/types/elearning";
import { imageMarkdown } from "@/utils/elearning/markdown";

/** Fila editable de una sección (el `orden` se deriva de la posición al guardar). */
interface SectionDraft {
  title: string;
  content: string;
  /** Vista previa del Markdown abierta para esta sección. */
  preview: boolean;
}

export interface ModuleSectionsEditorProps {
  /** Secciones actuales del módulo (resumen de contenido). */
  initial: ContentSection[];
  /** Persiste el set completo de secciones (reemplazo); el `orden` va por posición. */
  onSave: (sections: ContentSection[]) => void;
  /** Estado de guardado en curso (bloquea el botón). */
  saving?: boolean;
}

const inputClass =
  "w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-background text-sm text-foreground";
const iconButtonClass =
  "w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-muted-foreground hover:text-foreground disabled:opacity-40";
const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp";

/**
 * @name ModuleSectionsEditor
 * @description Editor del resumen de contenido del módulo (G5): lista ordenada de
 * secciones (título + contenido en Markdown acotado). Es material de lectura del
 * alumno; no condiciona la completitud. Guarda el set completo por reemplazo
 * (`PUT …/sections`); el orden se toma de la posición en la lista.
 *
 * El contenido admite subtítulos, listas, negrita/cursiva e imágenes en línea:
 * "Insertar imagen" sube el archivo al módulo files (misma vía que la imagen de una
 * pregunta) y agrega al final del texto la línea `![](url "size=md")`, que después
 * se puede mover o ajustar (`size=sm|md|lg|full`, leyenda). La vista previa
 * renderiza el mismo componente que ve el alumno.
 *
 * @param {ModuleSectionsEditorProps} props - Secciones iniciales, callback de guardado y estado.
 * @returns {JSX.Element} El editor renderizado.
 */
const ModuleSectionsEditor = ({
  initial,
  onSave,
  saving = false
}: ModuleSectionsEditorProps): JSX.Element => {
  const { t } = useLayout();
  const [sections, setSecciones] = useState<SectionDraft[]>(() =>
    [...initial]
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ title: s.title, content: s.content, preview: false }))
  );

  const update = (index: number, patch: Partial<SectionDraft>) => {
    setSecciones((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s))
    );
  };

  const addSection = () => {
    setSecciones((prev) => [
      ...prev,
      { title: "", content: "", preview: false }
    ]);
  };

  const removeSection = (index: number) => {
    setSecciones((prev) => prev.filter((_, i) => i !== index));
  };

  const move = (index: number, delta: number) => {
    setSecciones((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  /** Agrega la imagen recién subida como último bloque del contenido. */
  const insertImage = (index: number, url: string) => {
    setSecciones((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        const base = s.content.trimEnd();
        return {
          ...s,
          content: `${base ? `${base}\n\n` : ""}${imageMarkdown(url)}\n`
        };
      })
    );
  };

  const handleSave = () => {
    const limpias = sections
      .map((s) => ({ title: s.title.trim(), content: s.content.trim() }))
      .filter((s) => s.title && s.content)
      .map((s, index) => ({ order: index, ...s }));
    onSave(limpias);
  };

  return (
    <View className="flex flex-col gap-4">
      {sections.length === 0 ? (
        <Text className="text-sm text-muted-foreground">
          {t("noSectionsYet")}
        </Text>
      ) : (
        sections.map((seccion, index) => (
          <View
            // biome-ignore lint/suspicious/noArrayIndexKey: filas reordenables sin id estable
            key={index}
            className="flex flex-col gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-800"
          >
            <View className="flex items-center justify-between gap-2">
              <Text className="text-xs font-medium text-muted-foreground">
                {`${t("section")} ${index + 1}`}
              </Text>
              <View className="flex items-center gap-1">
                <Button
                  action={() => move(index, -1)}
                  disabled={index === 0}
                  label={t("moveUp")}
                  className={iconButtonClass}
                >
                  <Icon name="fa-solid fa-arrow-up" className="text-xs!" />
                </Button>
                <Button
                  action={() => move(index, 1)}
                  disabled={index === sections.length - 1}
                  label={t("moveDown")}
                  className={iconButtonClass}
                >
                  <Icon name="fa-solid fa-arrow-down" className="text-xs!" />
                </Button>
                <Button
                  action={() => removeSection(index)}
                  label={t("removeSection")}
                  className="w-8 h-8 rounded-lg border border-destructive/40 text-destructive hover:bg-destructive/10"
                >
                  <Icon name="fa-solid fa-trash-can" className="text-xs!" />
                </Button>
              </View>
            </View>
            <Input
              id={`seccion-title-${index}`}
              name={`seccion-title-${index}`}
              value={seccion.title}
              onChange={(e) => update(index, { title: e.target.value })}
              placeholder={t("sectionTitle")}
              className={inputClass}
            />
            {seccion.preview ? (
              <View className="p-4 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 min-h-[6rem]">
                {seccion.content.trim() ? (
                  <MarkdownContent content={seccion.content} />
                ) : (
                  <Text className="text-sm text-muted-foreground">
                    {t("sectionContent")}
                  </Text>
                )}
              </View>
            ) : (
              <textarea
                id={`seccion-content-${index}`}
                name={`seccion-content-${index}`}
                value={seccion.content}
                onChange={(e) => update(index, { content: e.target.value })}
                placeholder={t("sectionContent")}
                rows={8}
                className={`${inputClass} resize-y font-mono`}
              />
            )}
            <View className="flex flex-wrap items-center gap-3">
              <Button
                action={() => update(index, { preview: !seccion.preview })}
                label={seccion.preview ? t("sectionEdit") : t("sectionPreview")}
                className="px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-xs font-medium text-foreground hover:border-primary/50"
              >
                <Icon
                  name={seccion.preview ? "fa-solid fa-pen" : "fa-solid fa-eye"}
                  className="text-xs! mr-2"
                />
                {seccion.preview ? t("sectionEdit") : t("sectionPreview")}
              </Button>
              <FileUploadField
                id={`seccion-${index}-nueva-imagen`}
                name={`seccion-${index}-nueva-imagen`}
                accept={IMAGE_ACCEPT}
                label={t("sectionInsertImage")}
                onUploaded={(url) => insertImage(index, url)}
              />
            </View>
            <Text className="text-xs text-muted-foreground">
              {t("sectionMarkdownHint")}
            </Text>
          </View>
        ))
      )}

      <View className="flex items-center gap-3">
        <Button
          action={addSection}
          label={t("addSection")}
          className="px-4 py-2 rounded-full border border-slate-200 dark:border-slate-700 text-sm font-medium text-foreground hover:border-primary/50"
        >
          <Icon name="fa-solid fa-plus" className="text-xs! mr-2" />
          {t("addSection")}
        </Button>
        <Button
          action={handleSave}
          loading={saving}
          disabled={saving}
          label={t("save")}
          className="px-5 py-2.5 rounded-full bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {t("save")}
        </Button>
      </View>
    </View>
  );
};

export default ModuleSectionsEditor;
